/**
 * AlertsToolbar — Iter82
 *
 * The compact row that lives above the Journal d'alertes. Contains:
 *   • search field       (data-testid="alerts-search")
 *   • Filtres (n) button (data-testid="alerts-open-filters")
 *   • sort <select>      (data-testid="alerts-sort")
 *   • result count       (data-testid="alerts-result-count")
 *
 * Wraps to two lines on mobile; horizontal on desktop.
 */
import React from "react";
import { Search, SlidersHorizontal, X, ArrowDownWideNarrow, Loader2 } from "lucide-react";

const SORT_OPTIONS = [
  { value: "newest",  label: "Plus récentes" },
  { value: "oldest",  label: "Plus anciennes" },
  { value: "urgent",  label: "Plus urgentes" },
  { value: "waiting", label: "Attente la plus longue" },
  { value: "updated", label: "Dernière mise à jour" },
];

export const AlertsToolbar = ({
  search, onSearchChange, onSearchClear,
  activeFilterCount = 0,
  onOpenFilters,
  sort, onSortChange,
  totalMatching = 0,
  loading = false,
}) => {
  return (
    <div
      className="border border-zinc-800 bg-[#0f1318] p-2 flex flex-wrap items-center gap-2"
      data-testid="alerts-toolbar"
    >
      {/* Search field — grows, minimum 200px */}
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" aria-hidden />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Rechercher une alerte…"
          aria-label="Rechercher une alerte"
          data-testid="alerts-search"
          className="w-full bg-[#0b0f14] border border-zinc-700 text-zinc-100 pl-7 pr-8 py-1.5 text-[12px] font-mono focus:border-emerald-500 focus:outline-none"
        />
        {search && (
          <button
            type="button"
            onClick={onSearchClear}
            data-testid="alerts-search-clear"
            aria-label="Effacer la recherche"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-500 hover:text-rose-300"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filtres button */}
      <button
        type="button"
        onClick={onOpenFilters}
        data-testid="alerts-open-filters"
        className={`border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors ${
          activeFilterCount > 0
            ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
            : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
        }`}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
        Filtres
        {activeFilterCount > 0 && (
          <span
            data-testid="alerts-active-count"
            className="ml-0.5 bg-emerald-500 text-black px-1.5 py-0.5 text-[9px] font-black tabular-nums"
          >
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* Sort */}
      <label className="flex items-center gap-1.5 border border-zinc-700 pl-2.5 pr-1 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-300">
        <ArrowDownWideNarrow className="w-3.5 h-3.5 text-zinc-500" aria-hidden />
        <span className="sr-only">Trier par</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          data-testid="alerts-sort"
          aria-label="Trier par"
          className="bg-transparent text-zinc-200 text-[11px] font-bold uppercase tracking-[0.15em] focus:outline-none appearance-none pr-1"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#0f1318] normal-case">
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {/* Result count */}
      <div
        className="ml-auto text-[11px] font-mono text-zinc-500 tabular-nums flex items-center gap-1.5"
        data-testid="alerts-result-count"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        {totalMatching} alertes trouvées
      </div>
    </div>
  );
};

export default AlertsToolbar;
